"use client";

import { useState, useMemo, useEffect } from "react";

/** No debe aparecer como destinatario ni como filtro de rol en envíos masivos */
const ADMIN_MESSAGING_EXCLUDE_EMAIL = "admin@aristas.com";

function isExcludedMessagingRecipient(profile) {
  if (!profile) return true;
  if (profile.role === "admin") return true;
  if (profile.role === "doctor" && profile.is_professional_enabled === false) {
    return true;
  }
  if (
    (profile.email || "").toLowerCase().trim() === ADMIN_MESSAGING_EXCLUDE_EMAIL
  ) {
    return true;
  }
  return false;
}

import { Checkbox } from "../components/common/Checkbox";
import * as utils from "../utils/utils";
import { RadioGroup, RadioGroupItem } from "../components/common/RadioGroup";
import Spinner from "./common/Spinner";
import {
  MessageSquare,
  Mail,
  Users,
  Send,
  Search,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  User,
  Phone,
  Loader2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { formatDateDMY } from "../lib/ScheduleUtils";

import Alert from "../components/common/Alert";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Calendar } from "../components/common/Calendar";
import { Textarea } from "../components/common/Textarea";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import useBrandTranslation from "../hooks/useBrandTranslation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";

export default function Messaging() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { profiles } = useData();
  const navigate = useNavigate();

  // Estados del formulario
  const [messageType, setMessageType] = useState("whatsapp"); // "whatsapp" o "email"
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  // Estados para cancelación de turnos
  const [cancellationType, setCancellationType] = useState(""); // "" | "vacaciones" | "fecha" (solo para WhatsApp)
  const [dateFrom, setDateFrom] = useState(null); // Para cancelación por vacaciones
  const [dateTo, setDateTo] = useState(null); // Para cancelación por vacaciones
  const [cancellationDate, setCancellationDate] = useState(null); // Para cancelación por fecha

  // Filtrar usuarios disponibles (excluir actual, admin y admin@aristas.com)
  const availableUsers = useMemo(() => {
    return profiles.filter(
      (profile) =>
        profile.id !== user?.id && !isExcludedMessagingRecipient(profile)
    );
  }, [profiles, user?.id]);

  // Quitar de la selección IDs que ya no son destinatarios válidos (p. ej. admin)
  useEffect(() => {
    const allowed = new Set(availableUsers.map((p) => p.id));
    setSelectedUserIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [availableUsers]);

  // Aplicar filtros de búsqueda y rol
  const filteredUsers = useMemo(() => {
    let filtered = [...availableUsers];

    // Filtro por búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (u) =>
          u.name.toLowerCase().includes(term) ||
          u.last_name.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          (u.phone && u.phone.toLowerCase().includes(term))
      );
    }

    // Filtro por rol
    if (selectedRole !== "all") {
      filtered = filtered.filter((u) => u.role === selectedRole);
    }

    return filtered.sort((a, b) =>
      `${a.name} ${a.last_name}`.localeCompare(`${b.name} ${b.last_name}`)
    );
  }, [availableUsers, searchTerm, selectedRole]);

  // Usuarios seleccionados con información completa (nunca admin / email sistema)
  const selectedUsers = useMemo(() => {
    return Array.from(selectedUserIds)
      .map((id) => profiles.find((p) => p.id === id))
      .filter((p) => p !== undefined && !isExcludedMessagingRecipient(p));
  }, [selectedUserIds, profiles]);

  // Validar usuarios según el tipo de mensaje
  const validUsers = useMemo(() => {
    if (messageType === "whatsapp") {
      return selectedUsers.filter(
        (user) => user.phone && user.phone.trim() !== ""
      );
    }
    return selectedUsers.filter(
      (user) => user.email && user.email.trim() !== ""
    );
  }, [selectedUsers, messageType]);

  const invalidUsers = useMemo(() => {
    return selectedUsers.filter((user) => !validUsers.includes(user));
  }, [selectedUsers, validUsers]);

  // Roles únicos para filtros (sin rol administrador)
  const availableRoles = useMemo(() => {
    const roles = new Set(
      availableUsers.map((u) => u.role).filter((r) => r && r !== "admin")
    );
    return Array.from(roles).sort();
  }, [availableUsers]);

  useEffect(() => {
    if (selectedRole !== "all" && !availableRoles.includes(selectedRole)) {
      setSelectedRole("all");
    }
  }, [availableRoles, selectedRole]);

  // Manejar selección individual
  const handleUserToggle = (userId) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  // Seleccionar/deseleccionar todos los usuarios filtrados
  const handleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      // Deseleccionar todos
      setSelectedUserIds(new Set());
    } else {
      // Seleccionar todos los filtrados
      setSelectedUserIds(new Set(filteredUsers.map((u) => u.id)));
    }
  };

  // Limpiar selección
  const handleClearSelection = () => {
    setSelectedUserIds(new Set());
  };

  // Enviar mensajes
  const handleSendMessages = async () => {
    if (validUsers.length === 0) {
      setSendResult({
        success: false,
        message: "No hay usuarios válidos para enviar mensajes",
      });
      return;
    }

    // Validaciones según el tipo de mensaje
    if (messageType === "email") {
      if (!subject.trim() || !messageBody.trim()) {
        setSendResult({
          success: false,
          message: "Por favor completa el asunto y el mensaje",
        });
        return;
      }
    } else if (messageType === "whatsapp") {
      if (!cancellationType) {
        setSendResult({
          success: false,
          message: "Por favor selecciona un tipo de cancelación",
        });
        return;
      }
      if (cancellationType === "vacaciones") {
        if (!dateFrom || !dateTo) {
          setSendResult({
            success: false,
            message: "Por favor completa las fechas de inicio y fin de vacaciones",
          });
          return;
        }
      } else if (cancellationType === "fecha") {
        if (!cancellationDate) {
          setSendResult({
            success: false,
            message: "Por favor selecciona la fecha de cancelación",
          });
          return;
        }
      }
    }

    setSending(true);
    setSendResult(null);

    try {
      const requestBody = {
        messageType,
        senderId: user?.id, // ID del usuario que envía el mensaje (doctor o secretaria)
        recipients: validUsers.map((user) => ({
          id: user.id,
          name: `${user.name} ${user.last_name}`,
          email: user.email,
          phone: user.phone,
        })),
      };

      // Agregar datos según el tipo de mensaje
      if (messageType === "email") {
        requestBody.subject = subject.trim();
        requestBody.message = messageBody.trim();
      } else if (messageType === "whatsapp") {
        requestBody.cancellationType = cancellationType;
        if (cancellationType === "vacaciones") {
          requestBody.dateFrom = dateFrom;
          requestBody.dateTo = dateTo;
        } else if (cancellationType === "fecha") {
          requestBody.cancellationDate = cancellationDate;
        }
      }

      const response = await fetch("/api/send-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (response.ok) {
        setSendResult({
          success: true,
          message: `Mensajes enviados exitosamente`,
          details: result.details,
        });
        // Limpiar formulario después del envío exitoso
        setSubject("");
        setMessageBody("");
        setSelectedUserIds(new Set());
        if (messageType === "whatsapp") {
          setCancellationType("");
          setDateFrom(null);
          setDateTo(null);
          setCancellationDate(null);
        }
      } else {
        throw new Error(result.error || t("app.messaging.sendMessagesError", "Error al enviar mensajes"));
      }
    } catch (error) {
      console.error("Error sending messages:", error);
      setSendResult({
        success: false,
        message:
          error instanceof Error ? error.message : "Error al enviar mensajes",
      });
    } finally {
      setSending(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      doctor: "Médico",
      patient: "Paciente",
      secretary: "Secretaria",
      institution: "Institución",
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      doctor: "bg-blue-100 text-blue-800",
      patient: "bg-green-100 text-green-800",
      secretary: "bg-purple-100 text-purple-800",
      institution: "bg-orange-100 text-orange-800",
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  // Obtener datos del doctor para la preview
  const getDoctorData = () => {
    // Si el usuario actual es doctor, usar sus datos
    if (user?.role === "doctor") {
      return {
        name: user.name || "Dr.",
        last_name: user.last_name || "",
        phone: user.phone || "N/A",
        specialization: user.specialization || "N/A",
      };
    }
    // Si es secretaria, buscar el doctor asociado (por ahora usar datos del usuario o valores por defecto)
    // En una implementación completa, se podría buscar en la base de datos
    return {
      name: user?.name || "Dr.",
      last_name: user?.last_name || "",
      phone: user?.phone || "N/A",
      specialization: user?.specialization || "N/A",
    };
  };

  // Generar preview para WhatsApp - Cancelación por vacaciones
  const getWhatsAppVacacionesPreview = () => {
    if (!dateFrom || !dateTo) return null;
    
    const doctor = getDoctorData();
    const formattedDateFrom = formatDateDMY(dateFrom);
    const formattedDateTo = formatDateDMY(dateTo);

    const template = `Cancelacion de Turno

¡Hola! Esperamos que tu día marche bien. 

El ${doctor.name} ${doctor.last_name} ${doctor.specialization} se encontrara de vacaciones desde el dia ${formattedDateFrom} hasta el dia ${formattedDateTo}.

Los turnos entre esas fechas seran cancelados.

Si tenes alguna duda o consulta podes contactarte al ${doctor.phone}.

¡Lamentamos los inconvenientes ocasionados!

BINAPSIS`;

    return template;
  };

  // Generar preview para WhatsApp - Cancelación por fecha
  const getWhatsAppFechaPreview = () => {
    if (!cancellationDate) return null;
    
    const doctor = getDoctorData();
    const formattedDate = formatDateDMY(cancellationDate);

    const template = `Cancelacion de Turno

¡Hola! Esperamos que tu día marche bien. Los turnos para el dia ${formattedDate} han sido cancelados.

Si tenes alguna duda o consulta podes contactarte al ${doctor.phone}.

¡Lamentamos los inconvenientes ocasionados!

BINAPSIS`;

    return template;
  };

  if (!user) return <Spinner className="w-10 h-10 mx-auto" />;

  if (user && user.role !== "doctor" && user.role !== "secretary") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.messaging.accessRequired", "Acceso Requerido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Debes iniciar sesión para acceder al envío masivo de mensajes.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t("app.messaging.title", "Envío Masivo de Mensajes")}
          </h1>
          <p className="text-gray-600">
            {t("app.messaging.subtitle", "Envía mensajes por WhatsApp o Email a múltiples usuarios")}
          </p>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 pt-5">
                <Users className="w-5 h-5 text-blue-600" />
                <div className="flex items-center gap-2 justify-center">
                  <p className="text-2xl font-bold">{availableUsers.length}</p>
                  <p className="text-sm text-gray-600">{t("app.messaging.availableUsers", "Usuarios Disponibles")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 pt-5">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div className="flex items-center gap-2 justify-center">
                  <p className="text-2xl font-bold">{selectedUsers.length}</p>
                  <p className="text-sm text-gray-600">{t("app.messaging.selected", "Seleccionados")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 pt-5">
                {messageType === "whatsapp" ? (
                  <MessageSquare className="w-5 h-5 text-green-600" />
                ) : (
                  <Mail className="w-5 h-5 text-blue-600" />
                )}
                <div className="flex items-center gap-2 justify-center">
                  <p className="text-2xl font-bold">{validUsers.length}</p>
                  <p className="text-sm text-gray-600">{t("app.messaging.validForSend", "Válidos para Envío")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 pt-5">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <div className="flex items-center gap-2 justify-center">
                  <p className="text-2xl font-bold">{invalidUsers.length}</p>
                  <p className="text-sm text-gray-600">{t("app.messaging.noContactData", "Sin Datos de Contacto")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-14">
          {/* Panel de selección de usuarios */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Seleccionar Destinatarios
                  </CardTitle>
                  <div className="flex flex-col items-end gap-2 mr-2">
                    
                    {
                      selectedUserIds.size !== filteredUsers.length && (
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Seleccionar todos
                    </Button>
                      )
                    }
                    
                    {selectedUsers.length > 0 && (
                      <div className="flex items-center gap-2 pr-2" onClick={handleClearSelection}>
                        <X className="w-4 h-4 mr-1" />
                        Limpiar
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtros */}
                <div className="space-y-4 mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder={t("app.messaging.searchUsers", "Buscar por nombre, email o teléfono...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedRole === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedRole("all")}
                    >
                      Todos los roles
                    </Button>
                    {availableRoles.map((role) => (
                      <Button
                        key={role}
                        variant={selectedRole === role ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedRole(role)}
                      >
                        {getRoleLabel(role)}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Lista de usuarios */}
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {filteredUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">
                        No se encontraron usuarios
                      </p>
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <UserCard
                        key={user.id}
                        user={user}
                        isSelected={selectedUserIds.has(user.id)}
                        onToggle={handleUserToggle}
                        messageType={messageType}
                        getRoleColor={getRoleColor}
                        getRoleLabel={getRoleLabel}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Panel de configuración del mensaje */}
          <div className="space-y-6">
            {/* Tipo de mensaje */}
            <Card>
              <CardHeader>
                <CardTitle>{t("app.messaging.sendType", "Tipo de Envío")}</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={messageType}
                  onValueChange={(value) => {
                    setMessageType(value);
                    // Limpiar campos al cambiar el tipo de mensaje
                    if (value === "email") {
                      setCancellationType("");
                      setDateFrom(null);
                      setDateTo(null);
                      setCancellationDate(null);
                    } else if (value === "whatsapp") {
                      setSubject("");
                      setMessageBody("");
                    }
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="whatsapp" id="whatsapp" />
                    <Label
                      htmlFor="whatsapp"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <MessageSquare className="w-4 h-4 text-green-600" />
                      WhatsApp
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="email" />
                    <Label
                      htmlFor="email"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                      Email
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Formulario del mensaje */}
            <Card className="mb-20">
              <CardHeader>
                <CardTitle>{t("app.messaging.messageContent", "Contenido del Mensaje")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selector de tipo de cancelación solo para WhatsApp */}
                {messageType === "whatsapp" && (
                  <div>
                    <Label htmlFor="cancellation-type">{t("app.messaging.cancellationType", "Tipo de Cancelación *")}</Label>
                    <Select
                      value={cancellationType}
                      onValueChange={(value) => {
                        setCancellationType(value);
                        // Limpiar campos cuando cambia el tipo
                        setDateFrom(null);
                        setDateTo(null);
                        setCancellationDate(null);
                      }}
                    >
                      <SelectTrigger id="cancellation-type">
                        <SelectValue placeholder={t("app.messaging.selectCancellation", "Selecciona un tipo de cancelación")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vacaciones">{t("app.messaging.cancelVacation", "Cancelación de Turno por vacaciones")}</SelectItem>
                        <SelectItem value="fecha">{t("app.messaging.cancelDate", "Cancelación de Turno por fecha")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Campos para cancelación por vacaciones */}
                {messageType === "whatsapp" && cancellationType === "vacaciones" && (
                  <>
                    <div>
                      <Label htmlFor="date-from">{t("app.messaging.dateFrom", "Fecha desde *")}</Label>
                      <Input
                        id="date-from"
                        type="date"
                        value={dateFrom || ""}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="date-to">{t("app.messaging.dateTo", "Fecha hasta *")}</Label>
                      <Input
                        id="date-to"
                        type="date"
                        value={dateTo || ""}
                        onChange={(e) => setDateTo(e.target.value)}
                        min={dateFrom || ""}
                      />
                    </div>
                  </>
                )}

                {/* Campo para cancelación por fecha */}
                {messageType === "whatsapp" && cancellationType === "fecha" && (
                  <div>
                    <Label htmlFor="cancellation-date">{t("app.messaging.date", "Fecha *")}</Label>
                    <Input
                      id="cancellation-date"
                      type="date"
                      value={cancellationDate || ""}
                      onChange={(e) => setCancellationDate(e.target.value)}
                    />
                  </div>
                )}

                {/* Vista previa para WhatsApp - Cancelación por vacaciones */}
                {messageType === "whatsapp" && cancellationType === "vacaciones" && getWhatsAppVacacionesPreview() && (
                  <div className="mt-4">
                    <Label>{t("app.messaging.messagePreview", "Vista Previa del Mensaje")}</Label>
                    <Card className="mt-2 bg-green-50 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2 mb-2">
                          <MessageSquare className="w-4 h-4 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-green-800 mb-2">WhatsApp</p>
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">
                              {getWhatsAppVacacionesPreview()}
                            </pre>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Vista previa para WhatsApp - Cancelación por fecha */}
                {messageType === "whatsapp" && cancellationType === "fecha" && getWhatsAppFechaPreview() && (
                  <div className="mt-4">
                    <Label>{t("app.messaging.messagePreview", "Vista Previa del Mensaje")}</Label>
                    <Card className="mt-2 bg-green-50 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2 mb-2">
                          <MessageSquare className="w-4 h-4 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-green-800 mb-2">WhatsApp</p>
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">
                              {getWhatsAppFechaPreview()}
                            </pre>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Campos de asunto y mensaje (solo para email) */}
                {messageType === "email" && (
                  <>
                    <div>
                      <Label htmlFor="subject">{t("app.messaging.subject", "Asunto *")}</Label>
                      <Input
                        id="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={
                          messageType === "whatsapp"
                            ? "Título del mensaje"
                            : "Asunto del email"
                        }
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {subject.length}/100 caracteres
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="message">{t("app.messaging.message", "Mensaje *")}</Label>
                      <Textarea
                        id="message"
                        value={messageBody}
                        onChange={(e) => setMessageBody(e.target.value)}
                        placeholder={t("app.messaging.messagePlaceholder", "Escribe tu mensaje aquí...")}
                        rows={6}
                        maxLength={1000}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {messageBody.length}/1000 caracteres
                      </p>
                    </div>

                    {/* Vista previa para Email */}
                    {subject.trim() && messageBody.trim() && (
                      <div className="mt-4">
                        <Label>{t("app.messaging.emailPreview", "Vista Previa del Email")}</Label>
                        <Card className="mt-2 bg-blue-50 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-2 mb-2">
                              <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-blue-800 mb-1">{t("app.messaging.subjectLabel", "Asunto:")}</p>
                                <p className="text-sm text-gray-700 mb-3">{subject}</p>
                                <p className="text-sm font-semibold text-blue-800 mb-1">{t("app.messaging.messageLabel", "Mensaje:")}</p>
                                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">
                                  {messageBody}
                                </pre>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </>
                )}

                {/* Vista previa de destinatarios válidos/inválidos */}
                {selectedUsers.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium">
                        {validUsers.length} destinatario
                        {validUsers.length !== 1 ? "s" : ""} válido
                        {validUsers.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {invalidUsers.length > 0 && (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-orange-600">
                          {invalidUsers.length} sin{" "}
                          {messageType === "whatsapp" ? "teléfono" : "email"}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Resultado del envío */}
                {sendResult && (
                  <Alert
                    variant={sendResult.success ? "default" : "destructive"}
                  >
                    <AlertCircle className="h-4 w-4" />
                    <div>
                      {sendResult.message}
                      {sendResult.details && (
                        <div className="mt-2 text-sm">
                          Enviados: {sendResult.details.sent} | Fallidos:{" "}
                          {sendResult.details.failed}
                        </div>
                      )}
                    </div>
                  </Alert>
                )}

                {/* Botón de envío */}
                <Button
                  onClick={handleSendMessages}
                  disabled={
                    sending ||
                    validUsers.length === 0 ||
                    (messageType === "email" && (!subject.trim() || !messageBody.trim())) ||
                    (messageType === "whatsapp" && !cancellationType) ||
                    (messageType === "whatsapp" && cancellationType === "vacaciones" && (!dateFrom || !dateTo)) ||
                    (messageType === "whatsapp" && cancellationType === "fecha" && !cancellationDate)
                  }
                  className="w-full"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar a {validUsers.length} destinatario
                      {validUsers.length !== 1 ? "s" : ""}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserCard({
  user,
  isSelected,
  onToggle,
  messageType,
  getRoleColor,
  getRoleLabel,
}) {
  const hasValidContact =
    messageType === "whatsapp" ? user.phone?.trim() : user.email?.trim();

  return (
    <div
      className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer hover:bg-gray-50 ${
        isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200"
      } ${!hasValidContact ? "opacity-60" : ""}`}
      onClick={() => onToggle(user.id)}
    >
      <Checkbox checked={isSelected} onChange={() => onToggle(user.id)} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-sm">
            {user.name} {user.last_name}
          </span>
          {user.role && user.role !== "admin" && (
            <Badge className={`text-xs ${getRoleColor(user.role)}`}>
              {getRoleLabel(user.role)}
            </Badge>
          )}
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Mail className="w-3 h-3" />
            <span className="truncate">{user.email}</span>
          </div>
          {user.phone && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Phone className="w-3 h-3" />
              <span>{user.phone}</span>
            </div>
          )}
        </div>

        {!hasValidContact && (
          <div className="flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3 text-orange-500" />
            <span className="text-xs text-orange-600">
              Sin {messageType === "whatsapp" ? "teléfono" : "email"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
